import { useEffect, useRef } from 'react';
import { useApiContext, useGetFullInfo, useGetUserCountry, useUpdateInfo } from 'react-easyrocket';
import { useLocation, useNavigate } from 'react-router';
import { COUNTRY_TO_LANG, LS_DEBUG_LANGUAGE_KEY, LS_LANGUAGE_KEY } from '../config';
import { changeLanguage } from '../utils/changeLanguage';
import { extractLanguageFromUrl } from '../utils/extractLanguageFromUrl';
import { getBrowserLanguage } from '../utils/getBrowserLanguage';
import { isLanguageSupported } from '../utils/isLanguageSupported';
import { useSetLanguageToUrl } from './useSetLanguageToUrl';

const isDebugMode = localStorage.getItem(LS_DEBUG_LANGUAGE_KEY);

/**
 * Хук автоматически определяет язык на старте приложения.
 * Дальнейшее переключения языка происходит через языковой селектор.
 * Приоритеты подстановки языка:
 * - A: если пользователь авторизирован и есть данные о языке из профиля
 * - B: если в URL явно указан англиский - удаляем его из URL
 * - C: eсли язык есть в URL
 * - D: eсли язык есть localstorage
 * - E: язык по гео
 * - F: фоллбэк - язык браузера или en
 */
export const useInitLanguageUrl = () => {
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();

  // languageInitialized - устанавливаем язык из профиля 1 раз
  // т.к. useGetFullInfo заивит от языка, то при смене языка в кэше ничего нет
  // Это приводит к Race Condition - причина частых срабатываний эффекта.
  const languageInitialized = useRef(false);

  const { isAuthenticated } = useApiContext();
  const setLanguageToUrl = useSetLanguageToUrl();
  const { userCountry, isFetching: isUserCountryFetching } = useGetUserCountry();
  const { mutateAsync: updateProfileInfo } = useUpdateInfo();
  const { fullInfo } = useGetFullInfo();
  const langFromProfile = fullInfo?.language;
  const isLangFromProfileSupported = isLanguageSupported(langFromProfile);
  const country = userCountry?.country;

  // На случай, если в профиле играка записан уже не существующий язык,
  const updateProfileLang = (lang: string) => {
    if (isAuthenticated && !isLangFromProfileSupported) {
      if (isDebugMode) {
        console.debug('записали язык в Url');
      }
      languageInitialized.current = true;
      updateProfileInfo({
        language: lang
      });
    }
  };

  useEffect(() => {
    // Сбрасываем инициализацию языка при логауте
    if (!isAuthenticated && languageInitialized.current) {
      languageInitialized.current = false;
    }

    // Отменяем вызов эффекта если:
    // 1) Выполняется запрос на получение userCountry
    // 2) Пользователь авторизован, но fullInfo с языком еще не пришло,
    // или язук уже был один раз инициализирован (languageInitialized) через профиль
    if (isUserCountryFetching) return;
    if (isAuthenticated && (!langFromProfile || languageInitialized.current)) return;

    const localStorageLang = localStorage.getItem(LS_LANGUAGE_KEY);
    const langFromUrl = extractLanguageFromUrl(pathname);

    // A: если пользователь авторизирован и есть данные о языке из профиля
    if (isAuthenticated && langFromProfile && isLangFromProfileSupported) {
      if (isDebugMode) {
        console.debug('A: если пользователь авторизирован и есть данные о языке из профиля');
      }
      setLanguageToUrl(langFromProfile);
      changeLanguage(langFromProfile);
      languageInitialized.current = true;
      return;
    }

    // B: если в URL явно указан англиский - удаляем его из URL
    if (langFromUrl === 'en') {
      if (isDebugMode) {
        console.debug('B: если в URL явно указан англиский - удаляем его из URL');
      }
      changeLanguage('en');
      navigate(pathname.replace(/en\/?/, '') + search, { replace: true, state });
      return;
    }

    /**
     * C: если язык есть в URL
     */
    if (langFromUrl && isLanguageSupported(langFromUrl)) {
      if (isDebugMode) {
        console.debug('C: если язык есть в URL');
      }
      changeLanguage(langFromUrl);
      updateProfileLang(langFromUrl);
      return;
    }

    // D: eсли язык есть localstorage.
    if (!langFromUrl && localStorageLang && isLanguageSupported(localStorageLang)) {
      if (isDebugMode) {
        console.debug('D: eсли язык есть localstorage');
      }
      setLanguageToUrl(localStorageLang);
      changeLanguage(localStorageLang, false);
      updateProfileLang(localStorageLang);
      return;
    }

    // E: localstorage пустой = чистый вход => установить язык по гео.
    if (!localStorageLang && country) {
      if (isDebugMode) {
        console.debug('E: localstorage пустой = чистый вход');
      }
      const languageByGeo = COUNTRY_TO_LANG[country];
      if (languageByGeo) {
        console.debug('E: нашли страну - установили язык по гео');
        setLanguageToUrl(languageByGeo);
        changeLanguage(languageByGeo);
        updateProfileLang(languageByGeo);
        return;
      }
    }

    // F: язык все равно не был определен ни по одному из предыдущих кейсов
    // Чтобы кейс с выбором языка по гео работал (ожидает получения страны пользователя),
    // передаем в changeLanguage параметр doNotSaveToLS равный !country.
    if (isDebugMode) {
      console.debug('F: фоллбэк - язык браузера или en');
    }
    const browserLanguage = getBrowserLanguage();
    const languageToSet = isLanguageSupported(browserLanguage) ? browserLanguage : 'en';
    setLanguageToUrl(languageToSet);
    changeLanguage(languageToSet, !country);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, isAuthenticated, langFromProfile]);
};
